import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useCart } from '../../context/CartContext';
import { 
  XMarkIcon,
  ShoppingCartIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  GiftIcon
} from '@heroicons/react/24/outline';

export default function MainNavbar({ scrolled }) {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const { darkMode } = useTheme();
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const location = useLocation();
  const isHomepage = location.pathname === '/';
const topNavHeight = '40px'; 
  const isTransparent = isHomepage && !scrolled;
  const textColor = isTransparent ? 'text-white' : (darkMode ? 'text-white' : 'text-gray-900');
  const brandColor = '#e46809';
  const brandColorHover = '#d15e08';

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [menuOpen]);
  
  const activeLinkStyle = { color: brandColor, fontWeight: 'bold' };
  const badgeStyle = isTransparent 
    ? { backgroundColor: 'white', color: brandColor } 
    : { backgroundColor: brandColor, color: 'white' };
  const initials = (user?.first_name?.[0] || 'U').toUpperCase();

  return (
    <>
      <header className={`fixed w-full z-50 transition-all duration-300 ${
        isTransparent ? 'top-16 bg-transparent' : 'top-0 bg-white shadow-md dark:bg-gray-900'
      }`}>
        <nav className="container mx-auto px-4 py-3 flex items-center justify-between">
          
          <div className="flex-shrink-0">
            <Link to="/" title="Go to Homepage">
              <img src="/assets/logos/logo.svg" alt="Give Tech To the Blind Logo" className={`w-auto transition-all duration-300 ${scrolled ? 'h-14' : 'h-16'}`} />
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-8 font-medium">
            <NavLink to="/campaigns" className={`hover:text-[${brandColor}] transition-colors ${textColor}`} style={({isActive}) => isActive ? activeLinkStyle : undefined}>Campaigns</NavLink>
             <a href="/#success-stories" className={`hover:text-[${brandColor}] transition-colors ${textColor}`}>Success Stories</a>
            <NavLink to="/about" className={`hover:text-[${brandColor}] transition-colors ${textColor}`} style={({isActive}) => isActive ? activeLinkStyle : undefined}>About Us</NavLink>
          </div>
          
          <div className="flex-shrink-0 flex items-center">
            <div className="hidden lg:flex items-center space-x-4">
              <Link to="/search" className={`hover:text-[${brandColor}] transition-colors ${textColor} flex items-center whitespace-nowrap`} title="Search Campaigns"><MagnifyingGlassIcon className="h-5 w-5 mr-1" /><span>Search</span></Link>
              <div className={`h-6 w-px ${isTransparent ? 'bg-white/30' : 'bg-gray-300 dark:bg-gray-600'}`} />

              {user ? (
                <div className="relative" ref={dropdownRef}>
                  <button onClick={() => setDropdownOpen(!isDropdownOpen)} className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                    {initials}
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border dark:border-gray-700 py-1 z-50">
                      <div className="px-4 py-2 border-b dark:border-gray-600">
                        <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">Hi, {user.first_name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                      </div>
                      <NavLink to="/donor/account" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">My Account</NavLink>
                      <NavLink to="/donor/gifts" onClick={() => setDropdownOpen(false)} className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">My Gifts</NavLink>
                      <button onClick={logout} className="w-full text-left flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50">
                        <ArrowRightOnRectangleIcon className="w-5 h-5 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className={`hover:text-[${brandColor}] transition-colors ${textColor} flex items-center whitespace-nowrap`} title="Sign In or Register"><UserCircleIcon className="h-5 w-5 mr-1" /><span>Sign In</span></Link>
              )}
              
              <Link to="/cart" className={`relative hover:text-[${brandColor}] transition-colors ${textColor}`} title="View Your Gifting Bag">
                <ShoppingCartIcon className="h-6 w-6" />
                {itemCount > 0 && <span className="absolute -top-2 -right-2 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold" style={badgeStyle}>{itemCount}</span>}
              </Link>
              
              <Link
  to="/login"
  className={`px-4 py-2 rounded-full font-medium flex items-center whitespace-nowrap border-2 transition-colors duration-300 
     ${isTransparent 
      
      ? `border-white text-white hover:bg-white hover:text-orange-600` 
     
      : `border-orange-600 text-orange-600 hover:bg-orange-600 hover:text-white`
    }
  `} 
  title="Start a New Gift Campaign"
>
  <span>Start a Gift Campaign</span>
</Link>
            </div>
            
            <div className="lg:hidden flex items-center space-x-2">
                <Link to="/cart" className={`relative p-2 ${textColor} hover:text-[${brandColor}]`} title="View Your Gifting Bag">
                    <ShoppingCartIcon className="h-7 w-7" />
                    {itemCount > 0 && <span className="absolute top-0 right-0 text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold" style={badgeStyle}>{itemCount}</span>}
                </Link>
                <button onClick={toggleMenu} className={`p-2 ${textColor} hover:text-[${brandColor}]`} aria-label="Open Menu"><Bars3Icon className="h-7 w-7" /></button>
            </div>
          </div>
        </nav>
      </header>
      
      {menuOpen && (
        <div className="fixed inset-0 z-[9998] lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={closeMenu} />
          <div className={`absolute top-0 right-0 h-full w-4/5 max-w-sm bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="p-4 flex justify-between items-center border-b dark:border-gray-700">
              <h2 className="font-bold text-lg dark:text-white">Menu</h2>
              <button onClick={closeMenu} className="p-2 text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white" aria-label="Close menu"><XMarkIcon className="h-7 w-7" /></button>
            </div>
            <div className="p-4">
              <nav className="flex flex-col space-y-2">
                <Link to="/search" onClick={closeMenu} className="flex items-center px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><MagnifyingGlassIcon className="h-6 w-6 mr-3" /><span>Search</span></Link>
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                <Link to="/campaigns" onClick={closeMenu} className="px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Campaigns</Link>
                <Link to="/#success-stories" onClick={closeMenu} className="px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Success Stories</Link>
                <Link to="/about" onClick={closeMenu} className="px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">About Us</Link>
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                {user ? (
                    <>
                        <Link to="/donor/account" onClick={closeMenu} className="flex items-center px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><UserCircleIcon className="h-6 w-6 mr-3" /><span>My Account</span></Link>
                        <Link to="/donor/gifts" onClick={closeMenu} className="flex items-center px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><GiftIcon className="h-6 w-6 mr-3" /><span>My Gifts</span></Link>
                        <button onClick={() => { logout(); closeMenu(); }} className="flex items-center w-full px-4 py-3 text-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg"><ArrowRightOnRectangleIcon className="h-6 w-6 mr-3" /><span>Sign Out</span></button>
                    </>
                ) : (
                    <Link to="/login" onClick={closeMenu} className="flex items-center px-4 py-3 text-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><UserCircleIcon className="h-6 w-6 mr-3" /><span>Sign In</span></Link>
                )}
                <div className="border-t border-gray-200 dark:border-gray-700 my-2" />
                <Link to="/campaigns" onClick={closeMenu} className={`flex items-center justify-center px-4 py-3 text-lg font-semibold text-white bg-[${brandColor}] hover:bg-[${brandColorHover}] rounded-lg`}>
                  <span className="mr-2">Start a Gift</span>
                </Link>
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
}